import { useEffect, useRef, useState } from 'react'

import { useAuth } from '@/hooks'
import useApi from '@/hooks/useApi'
import { clearAllCaches } from '@/providers/apiService'
import { clearToken } from '@/services/authService'

import { matchUser, secondaryFields, type ImpersonateUser, type UserMatch } from './impersonateMatch'

import Modal from '../modal/Modal'

interface ImpersonateModalProps {
  show: boolean
  onHide: () => void
}

function getProviderLabel(uid: string): string {
  if (uid.startsWith('auth0')) return '(Cooltrax)'
  if (uid.startsWith('waad')) return '(Active Directory)'
  return ''
}

function HighlightedText({ text, indices }: { text: string; indices?: number[] }) {
  const set = new Set(indices ?? [])
  return (
    <span>
      {text.split('').map((char, i) =>
        set.has(i) ? (
          <mark key={i} className="bg-transparent text-info font-medium">
            {char}
          </mark>
        ) : (
          <span key={i}>{char}</span>
        )
      )}
    </span>
  )
}

export const ImpersonateModal: React.FC<ImpersonateModalProps> = ({ show, onHide }) => {
  const { user, originalUser } = useAuth()
  const { api } = useApi()

  const [sudoEmail, setSudoEmail] = useState('')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uid, setUid] = useState<string>()
  const [users, setUsers] = useState<ImpersonateUser[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    if (!user) return
    setUid(user.id)
    if (!originalUser || originalUser.email === user.email) {
      setQuery('')
      setSudoEmail('')
    } else {
      setQuery(user.email)
      setSudoEmail(user.email)
    }
  }, [user, originalUser])

  useEffect(() => {
    if (!show) return
    api
      .get<ImpersonateUser[]>({ path: '/app/users', origin: 'impersonate' })
      .then((result) => {
        if (!result.error) setUsers(result.data ?? [])
      })
      .catch(console.error)
  }, [show])

  const matches =
    query.length > 0
      ? users
          .map((u) => {
            const match = matchUser(u, query)
            return match ? { ...u, match } : null
          })
          .filter((u): u is ImpersonateUser & { match: UserMatch } => u !== null)
          .sort((a, b) => b.match.score - a.match.score)
          .slice(0, 8)
      : []

  const handleSelect = (u: ImpersonateUser) => {
    setQuery(u.email)
    setShowSuggestions(false)
    setActiveIdx(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || matches.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      handleSelect(matches[activeIdx])
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  const handleApply = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await api.post({
        path: `/app/profiles/${uid}/sudo`,
        origin: 'impersonate',
        body: { sudo_email: query.trim() },
      })
      if (result.error) {
        setError(result.message ?? 'Failed to update profile')
        setLoading(false)
        return
      }
      clearAllCaches()
      clearToken()
      window.location.reload()
    } catch (ex) {
      console.error(ex)
      setError('An unexpected error occurred')
      setLoading(false)
    }
  }

  const bodyContent = (
    <div className="flex flex-col gap-3">
      {sudoEmail && <div className="text-sm text-info">Currently impersonating: {sudoEmail}</div>}
      <label className="form-control w-full">
        <div className="label">
          <span className="text-base-content mb-2">Email to impersonate (leave blank to revert to yourself)</span>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search by email, name or username..."
            className="input input-bordered w-full pr-8 text-base-content"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setShowSuggestions(true)
              setActiveIdx(-1)
              setError(null)
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (query) setShowSuggestions(true)
            }}
            onBlur={() => {
              blurTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 150)
            }}
            disabled={loading}
            autoComplete="off"
          />
          {query && !loading && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content cursor-pointer"
              onClick={() => {
                setQuery('')
                setShowSuggestions(false)
              }}
              aria-label="Clear email"
            >
              &times;
            </button>
          )}
        </div>
      </label>

      {showSuggestions && matches.length > 0 && (
        <ul className="border border-base-300 rounded-lg overflow-hidden">
          {matches.map((u, i) => (
            <li
              key={u.uid}
              onMouseDown={() => {
                if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
                handleSelect(u)
              }}
              className={`flex cursor-pointer text-base-content items-center justify-between px-3 py-2 text-sm ${
                i === activeIdx ? 'bg-base-200' : 'hover:bg-base-200'
              }`}
            >
              <span className="min-w-0">
                <HighlightedText text={u.email} indices={u.match.text === u.email ? u.match.indices : undefined} />
                {secondaryFields(u).length > 0 && (
                  <span className="block truncate text-xs text-base-content/60">
                    {secondaryFields(u).map((field, fieldIdx) => (
                      <span key={field}>
                        {fieldIdx > 0 && <span> · </span>}
                        <HighlightedText text={field} indices={u.match.text === field ? u.match.indices : undefined} />
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <span className="ml-3 shrink-0 text-xs text-base-content/50">{getProviderLabel(u.uid)}</span>
            </li>
          ))}
        </ul>
      )}

      {showSuggestions && query && matches.length === 0 && (
        <div className="text-sm text-base-content/50 px-1">No matches found</div>
      )}

      {error && <div className="text-sm text-error">{error}</div>}
    </div>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      title="Impersonate User"
      bodyContent={bodyContent}
      footerButtons={[
        {
          label: loading ? 'Applying...' : 'Apply',
          onClick: handleApply,
          disabled: loading,
          useModalVariant: true,
        },
        {
          label: 'Cancel',
          onClick: onHide,
          disabled: loading,
        },
      ]}
    />
  )
}
